/**
 * API base URL + the strict JSON transport for the public (pre-session) pages.
 *
 * The deployed UI is a static bundle on one origin (CloudFront) and the API is
 * on another; `VITE_API_URL` is baked in at build time. Every request the UI
 * makes must be prefixed with it — a bare `fetch('/api/...')` works in dev only
 * because the Vite proxy hides it, and on the CDN it lands on the static host,
 * which answers every unknown path and method with index.html `200 text/html`.
 *
 * That second fact is why `postJson`/`getJson` are strict: an HTML 200 used to
 * pass `res.ok` and `res.json().catch(() => ({}))` and read as success (the
 * reset page navigated to the login screen while no password had changed).
 * Here, anything that is not a JSON body is an `ApiError` the page shows
 * inline — `NETWORK` when fetch throws, `BAD_RESPONSE` when the content type
 * is not JSON or the body does not parse — and navigation only ever happens
 * on a parsed JSON success.
 *
 * Authenticated calls go through TanStack Query + `authenticatedFetch`
 * (session-refresh retry) or the axios clients. The rule that no other module
 * may spell out a bare `/api/...` path is enforced twice: the ESLint
 * `no-restricted-syntax` entry in eslint.config.js (editor / pr-review) and
 * the source scan in `src/__tests__/noBareApiPaths.test.ts` (CI).
 */

import { API_TIMEOUT_CONFIG } from '../config/api';

/**
 * Prefix an API-relative path (`/admin/auth/login`) with the configured base.
 * Read from `import.meta.env` on every call (Vite inlines it at build time;
 * in tests `vi.stubEnv` must be observable without re-importing the module).
 */
export function apiUrl(path: string): string {
  const rawApiBaseUrl: string = import.meta.env.VITE_API_URL ?? '/api';
  const base = rawApiBaseUrl.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status?: number;
  /** The request field the server refused, when it names one (`{ field }`). */
  readonly field: string | null;

  constructor(code: string, message?: string, status?: number, field: string | null = null) {
    super(message ?? code);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.field = field;
  }
}

/** The error code of any thrown value: an ApiError's code, otherwise INTERNAL. */
export function errorCode(err: unknown): string {
  return err instanceof ApiError ? err.code : 'INTERNAL';
}

/**
 * True when the failure was the transport, not the server's answer. The copy
 * for it (and for every other failure) comes from `getErrorMessage` in
 * utils/errorUtils.ts.
 */
export function isServerUnreachable(err: unknown): boolean {
  const code = errorCode(err);
  return code === 'NETWORK' || code === 'TIMEOUT' || code === 'BAD_RESPONSE';
}

interface ServerErrorBody {
  error?: unknown;
  error_code?: unknown;
  code?: unknown;
  field?: unknown;
}

/**
 * Pull `{ code, message, field }` out of the error shapes the backend emits:
 *   routes:        { error: string, error_code: string }
 *   tenant switch: { error: string, code: string }
 *   rate limiter:  { error: { code: string, message: string, retryAfter } }
 */
function readServerError(body: unknown): { code: string; message: string | undefined; field: string | null } {
  const b = (typeof body === 'object' && body !== null ? body : {}) as ServerErrorBody;
  const nested =
    typeof b.error === 'object' && b.error !== null
      ? (b.error as { code?: unknown; message?: unknown })
      : undefined;
  const code =
    typeof b.error_code === 'string'
      ? b.error_code
      : typeof b.code === 'string'
        ? b.code
        : typeof nested?.code === 'string'
          ? nested.code
          : 'INTERNAL';
  const message =
    typeof b.error === 'string'
      ? b.error
      : typeof nested?.message === 'string'
        ? nested.message
        : undefined;
  return { code, message, field: typeof b.field === 'string' ? b.field : null };
}

/**
 * The error for a non-2xx `Response` from `authenticatedFetch` or a raw
 * `fetch`: an `ApiError` carrying the server's code, its message when the
 * body had one, and the status. Without a message, getErrorMessage maps the
 * status instead — a bare 500 reads "Something went wrong on our side.", a
 * 502/503/504 "Switchboard isn't responding…" — so no technical text such
 * as "(HTTP 500)" reaches the screen.
 */
export async function responseError(res: Response): Promise<ApiError> {
  const body: unknown = await res.json().catch(() => undefined);
  const { code, message, field } = readServerError(body);
  return new ApiError(code, message, res.status, field);
}

function isRead(init: RequestInit): boolean {
  const method = (init.method ?? 'GET').toUpperCase();
  return method === 'GET' || method === 'HEAD';
}

/**
 * `fetch` for both transports (`requestJson` here, `authenticatedFetch`). Reads
 * (GET/HEAD) give up after `API_TIMEOUT_CONFIG.readMs`, like the axios client,
 * so a hung server becomes an error on screen instead of an endless spinner.
 * A timeout rejects with a TIMEOUT `ApiError` and a refused connection with
 * a NETWORK one; a
 * caller's own cancellation (TanStack Query's signal) passes through as-is.
 */
export async function sendRequest(input: string, init: RequestInit): Promise<Response> {
  const callerSignal = init.signal ?? undefined;
  const controller = new AbortController();
  const onCallerAbort = () => { controller.abort(callerSignal?.reason); };
  if (callerSignal?.aborted) onCallerAbort();
  callerSignal?.addEventListener('abort', onCallerAbort);
  const timer = isRead(init)
    ? setTimeout(() => { controller.abort(new DOMException('Request timed out', 'TimeoutError')); }, API_TIMEOUT_CONFIG.readMs)
    : undefined;

  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (err) {
    if (callerSignal?.aborted) throw err;
    if (controller.signal.reason instanceof DOMException && controller.signal.reason.name === 'TimeoutError') {
      throw new ApiError('TIMEOUT', 'Request timed out');
    }
    throw new ApiError('NETWORK', err instanceof Error ? err.message : 'Network request failed');
  } finally {
    clearTimeout(timer);
    callerSignal?.removeEventListener('abort', onCallerAbort);
  }
}

/**
 * TanStack Query `retry` for queries on the fetch transports, which have no
 * retry layer of their own: one more attempt, except after a timeout — a hung
 * server has already cost `readMs`, and waiting again would double it.
 */
export function retryOnceUnlessTimedOut(failureCount: number, error: unknown): boolean {
  return failureCount < 1 && errorCode(error) !== 'TIMEOUT';
}

async function requestJson<T>(path: string, init: RequestInit): Promise<T> {
  const res = await sendRequest(apiUrl(path), { credentials: 'include', ...init });

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    throw new ApiError(
      'BAD_RESPONSE',
      `Expected a JSON response, got ${contentType || 'no content-type'} (HTTP ${res.status})`,
      res.status,
    );
  }

  let json: unknown;
  try {
    json = await res.json();
  } catch {
    throw new ApiError('BAD_RESPONSE', `Malformed JSON response (HTTP ${res.status})`, res.status);
  }

  if (!res.ok) {
    // No message in the body leaves ApiError.message === code, which
    // getErrorMessage maps from the status instead of showing it raw.
    const { code, message, field } = readServerError(json);
    throw new ApiError(code, message, res.status, field);
  }
  return json as T;
}

/**
 * POST a JSON body and return the parsed JSON response. Rejects with an
 * `ApiError` for a network failure (`NETWORK`), a non-JSON or unparsable
 * response (`BAD_RESPONSE`), or a non-2xx JSON body (the server's code).
 */
export function postJson<T>(path: string, body: unknown, init?: RequestInit): Promise<T> {
  return requestJson<T>(path, {
    ...init,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(init?.headers as Record<string, string> | undefined) },
    body: JSON.stringify(body),
  });
}

/** GET and return the parsed JSON response; same failure contract as `postJson`. */
export function getJson<T>(path: string, init?: RequestInit): Promise<T> {
  return requestJson<T>(path, { ...init, method: 'GET' });
}
